package co.maytes.standupbot.dashboard.pages.app.standups.sections

import androidx.compose.runtime.Composable
import androidx.compose.runtime.LaunchedEffect
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import co.maytes.standupbot.dashboard.components.feedback.SkeletonLine
import co.maytes.standupbot.dashboard.components.widgets.Button
import co.maytes.standupbot.dashboard.components.widgets.ButtonSize
import co.maytes.standupbot.dashboard.components.widgets.ButtonVariant
import co.maytes.standupbot.dashboard.components.widgets.DatePicker
import co.maytes.standupbot.dashboard.components.widgets.FormRow
import co.maytes.standupbot.dashboard.components.widgets.IconButton
import co.maytes.standupbot.dashboard.components.widgets.SectionCard
import co.maytes.standupbot.dashboard.components.widgets.SectionDivider
import co.maytes.standupbot.dashboard.components.widgets.Select
import co.maytes.standupbot.dashboard.components.widgets.SelectOption
import co.maytes.standupbot.dashboard.components.widgets.cssRgb
import co.maytes.standupbot.dashboard.lib.api.Result
import co.maytes.standupbot.dashboard.stores.rememberApiClient
import co.maytes.standupbot.dashboard.styles.Palette
import co.maytes.standupbot.dashboard.styles.Radius
import co.maytes.standupbot.dashboard.styles.Spacing
import co.maytes.standupbot.dashboard.styles.Typography
import co.maytes.standupbot.shared.dto.holiday.HolidayDto
import co.maytes.standupbot.shared.dto.holiday.HolidaysResponseDto
import co.maytes.standupbot.shared.dto.standup.StandupDto
import co.maytes.standupbot.shared.dto.standup.UpdateStandupRequestDto
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.modifiers.ariaHidden
import com.varabyte.kobweb.compose.ui.modifiers.backgroundColor
import com.varabyte.kobweb.compose.ui.modifiers.borderRadius
import com.varabyte.kobweb.compose.ui.modifiers.color
import com.varabyte.kobweb.compose.ui.modifiers.cursor
import com.varabyte.kobweb.compose.ui.modifiers.fillMaxWidth
import com.varabyte.kobweb.compose.ui.modifiers.flexWrap
import com.varabyte.kobweb.compose.ui.modifiers.fontFamily
import com.varabyte.kobweb.compose.ui.modifiers.fontSize
import com.varabyte.kobweb.compose.ui.modifiers.fontWeight
import com.varabyte.kobweb.compose.ui.modifiers.gap
import com.varabyte.kobweb.compose.ui.modifiers.margin
import com.varabyte.kobweb.compose.ui.modifiers.padding
import com.varabyte.kobweb.compose.ui.modifiers.width
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.silk.components.text.SpanText
import com.varabyte.kobweb.silk.style.toModifier
import kotlinx.coroutines.launch
import kotlinx.datetime.Clock
import kotlinx.datetime.LocalDate
import kotlinx.datetime.TimeZone
import kotlinx.datetime.toLocalDateTime
import org.jetbrains.compose.web.attributes.InputType
import org.jetbrains.compose.web.attributes.placeholder
import org.jetbrains.compose.web.css.FlexWrap
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Input

private val COUNTRY_OPTIONS: List<Pair<String, String>> = listOf(
    "AU" to "Australia",
    "CA" to "Canada",
    "DE" to "Germany",
    "ES" to "Spain",
    "FR" to "France",
    "GB" to "United Kingdom",
    "IN" to "India",
    "IT" to "Italy",
    "JP" to "Japan",
    "NL" to "Netherlands",
    "NZ" to "New Zealand",
    "US" to "United States",
)

@Composable
internal fun HolidaysCard(
    standup: StandupDto,
    workspaceId: String?,
    inFlight: Boolean,
    onSave: (UpdateStandupRequestDto) -> Unit,
) {
    var country by remember(standup.id) { mutableStateOf(standup.holidayCountry) }
    var subdivision by remember(standup.id) { mutableStateOf(standup.holidaySubdivision.orEmpty()) }
    var dates by remember(standup.id) {
        mutableStateOf(standup.extraSkipDates.toSet())
    }
    var addingDate by remember(standup.id) { mutableStateOf(false) }
    var draftDate by remember(standup.id) { mutableStateOf("") }

    val originalCountry = standup.holidayCountry
    val originalSubdivision = standup.holidaySubdivision
    val originalDates = standup.extraSkipDates.toSet()

    val normalizedSubdivision = subdivision.trim().ifBlank { null }
    val countryDirty = country != originalCountry
    val subdivisionDirty = normalizedSubdivision != originalSubdivision
    val datesDirty = dates != originalDates
    val dirty = countryDirty || subdivisionDirty || datesDirty

    val currentYear = remember {
        Clock.System.now().toLocalDateTime(TimeZone.currentSystemDefault()).year
    }
    var year by remember(standup.id) { mutableStateOf(currentYear) }
    val api = rememberApiClient()
    val previewScope = rememberCoroutineScope()
    var preview by remember { mutableStateOf<HolidaysResponseDto?>(null) }
    var previewLoading by remember { mutableStateOf(false) }
    var previewError by remember { mutableStateOf(false) }
    val previewCountry = country

    LaunchedEffect(workspaceId, previewCountry, normalizedSubdivision, year) {
        preview = null
        previewError = false
        if (workspaceId.isNullOrBlank() || previewCountry.isNullOrBlank()) {
            previewLoading = false
            return@LaunchedEffect
        }
        previewLoading = true
        previewScope.launch {
            val res = api.get<HolidaysResponseDto>(
                "/api/v1/workspaces/$workspaceId/holidays?country=$previewCountry" +
                    (normalizedSubdivision?.let { "&subdivision=$it" } ?: "") +
                    "&year=$year",
            )
            when (res) {
                is Result.Ok -> {
                    preview = res.value
                    previewError = false
                }
                is Result.Err -> {
                    preview = null
                    previewError = true
                }
            }
            previewLoading = false
        }
    }

    SectionCard(
        title = "Holidays",
        description = "Skip scheduled runs on public holidays and custom dates.",
    ) {
        Column(modifier = Modifier.fillMaxWidth().gap(Spacing.lg)) {
            HolidaySubsectionHeader(
                title = "Holiday rules",
                hint = "Pick a country to auto-skip its public holidays.",
            )

            FormRow(label = "Country") {
                Select(
                    value = country.orEmpty(),
                    options = buildList {
                        add(SelectOption("", "(none — never skip)"))
                        COUNTRY_OPTIONS.forEach { (code, name) ->
                            add(SelectOption(code, "$name ($code)"))
                        }
                    },
                    onValueChange = { v ->
                        country = v.ifBlank { null }
                        if (country == null) subdivision = ""
                    },
                    modifier = Modifier.fillMaxWidth(),
                    disabled = inFlight,
                    ariaLabel = "Holiday country",
                )
            }

            if (country != null) {
                FormRow(label = "Subdivision") {
                    Input(
                        type = InputType.Text,
                        attrs = co.maytes.standupbot.dashboard.components.widgets.TextInputStyle.toModifier()
                            .fillMaxWidth()
                            .toAttrs {
                                attr("aria-label", "Holiday subdivision")
                                placeholder("AU-NSW (optional)")
                                value(subdivision)
                                onInput { subdivision = it.value }
                                if (inFlight) attr("disabled", "disabled")
                            },
                    )
                }
            }

            SectionDivider()

            HolidaySubsectionHeader(
                title = "Custom dates",
                hint = "One-off dates to skip on top of public holidays.",
            )

            Column(modifier = Modifier.fillMaxWidth().gap(Spacing.sm)) {
                if (dates.isEmpty()) {
                    SpanText(
                        text = "No custom skip dates yet.",
                        modifier = Modifier
                            .fontSize(Typography.bodySmall.size)
                            .color(Palette.textTertiary),
                    )
                } else {
                    Row(modifier = Modifier.fillMaxWidth().flexWrap(FlexWrap.Wrap).gap(Spacing.xs)) {
                        dates.sorted().forEach { d ->
                            DateChip(
                                label = formatChipDate(d),
                                onRemove = { dates = dates - d },
                            )
                        }
                    }
                }
                if (addingDate) {
                    Row(
                        modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
                        verticalAlignment = Alignment.Bottom,
                    ) {
                        DatePicker(
                            value = draftDate,
                            onChange = { draftDate = it },
                            modifier = Modifier.styleModifier { property("flex", "1") },
                        )
                        Button(
                            label = "Add",
                            variant = ButtonVariant.Secondary,
                            size = ButtonSize.Sm,
                            enabled = draftDate.isNotBlank(),
                            onClick = {
                                val parsed = runCatching { LocalDate.parse(draftDate) }.getOrNull()
                                if (parsed != null) {
                                    dates = dates + parsed
                                    draftDate = ""
                                    addingDate = false
                                }
                            },
                        )
                        Button(
                            label = "Cancel",
                            variant = ButtonVariant.Ghost,
                            size = ButtonSize.Sm,
                            onClick = {
                                draftDate = ""
                                addingDate = false
                            },
                        )
                    }
                } else {
                    val nextRunDate = standup.nextRunAt?.let {
                        val tz = runCatching { TimeZone.of(standup.timezone) }.getOrDefault(TimeZone.UTC)
                        it.toLocalDateTime(tz).date
                    }
                    val canSkipNext =
                        standup.enabled && nextRunDate != null && nextRunDate !in dates
                    Row(
                        modifier = Modifier.styleModifier {
                            property("gap", "6px")
                            property("flex-wrap", "wrap")
                        },
                    ) {
                        Button(
                            label = "+ Add date",
                            variant = ButtonVariant.Ghost,
                            size = ButtonSize.Sm,
                            enabled = !inFlight,
                            onClick = { addingDate = true },
                        )
                        nextRunDate?.takeIf { canSkipNext }?.let { date ->
                            Button(
                                label = "+ Skip next run (${formatChipDate(date)})",
                                variant = ButtonVariant.Ghost,
                                size = ButtonSize.Sm,
                                enabled = !inFlight,
                                onClick = { dates = dates + date },
                            )
                        }
                    }
                }
                SpanText(
                    text = "Scheduled runs that fall on listed dates or public holidays for the selected country are skipped automatically. Manual 'Run now' triggers always fire.",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary),
                )
            }

            if (!previewCountry.isNullOrBlank()) {
                SectionDivider()
                HolidaySubsectionHeader(
                    title = "Preview",
                    hint = "Public holidays detected for the selected country.",
                )
                HolidayPreviewPanel(
                    country = previewCountry,
                    subdivision = normalizedSubdivision,
                    year = year,
                    currentYear = currentYear,
                    loading = previewLoading,
                    errored = previewError,
                    response = preview,
                    extraSkipDates = dates,
                    onYearChange = { year = it },
                )
            }

            Row(
                modifier = Modifier.fillMaxWidth().gap(Spacing.sm),
                horizontalArrangement = Arrangement.End,
                verticalAlignment = Alignment.CenterVertically,
            ) {
                Button(
                    label = "Reset",
                    variant = ButtonVariant.Ghost,
                    enabled = dirty && !inFlight,
                    onClick = {
                        country = originalCountry
                        subdivision = originalSubdivision.orEmpty()
                        dates = originalDates
                        draftDate = ""
                        addingDate = false
                    },
                )
                Button(
                    label = if (inFlight) "Saving…" else "Save holidays",
                    variant = ButtonVariant.Primary,
                    enabled = dirty && !inFlight,
                    onClick = {
                        onSave(
                            UpdateStandupRequestDto(
                                holidayCountry = country,
                                clearHolidayCountry = country == null && originalCountry != null,
                                holidaySubdivision = normalizedSubdivision,
                                clearHolidaySubdivision = normalizedSubdivision == null && originalSubdivision != null,
                                extraSkipDates = dates.sorted(),
                            ),
                        )
                    },
                )
            }
        }
    }
}

@Composable
private fun HolidaySubsectionHeader(title: String, hint: String) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.xxs)) {
        SpanText(
            text = title,
            modifier = Modifier
                .fontSize(Typography.label.size)
                .fontWeight(Typography.label.weight)
                .color(Palette.textPrimary),
        )
        SpanText(
            text = hint,
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textTertiary),
        )
    }
}

@Composable
private fun DateChip(label: String, onRemove: () -> Unit) {
    Row(
        modifier = Modifier
            .padding(topBottom = Spacing.xxs, leftRight = Spacing.xs)
            .backgroundColor(Palette.surfaceSubtle)
            .borderRadius(Radius.full)
            .styleModifier { property("border", "1px solid ${Palette.border.cssRgb()}") },
        verticalAlignment = Alignment.CenterVertically,
        horizontalArrangement = Arrangement.spacedBy(Spacing.xxs),
    ) {
        SpanText(
            text = label,
            modifier = Modifier
                .fontSize(Typography.bodySmall.size)
                .color(Palette.textPrimary),
        )
        IconButton(
            ariaLabel = "Remove holiday",
            modifier = Modifier.color(Palette.textTertiary),
            onClick = { onRemove() },
        ) {
            SpanText(
                "×",
                modifier = Modifier.fontSize(Typography.body.size).ariaHidden(),
            )
        }
    }
}

private val MONTH_ABBREV = arrayOf(
    "Jan", "Feb", "Mar", "Apr", "May", "Jun",
    "Jul", "Aug", "Sep", "Oct", "Nov", "Dec",
)

private val MONTH_ABBREV_UPPER = MONTH_ABBREV.map { it.uppercase() }

private fun formatChipDate(date: LocalDate): String =
    "${MONTH_ABBREV[date.monthNumber - 1]} ${date.dayOfMonth}, ${date.year}"

@Composable
private fun HolidayPreviewPanel(
    country: String,
    subdivision: String?,
    year: Int,
    currentYear: Int,
    loading: Boolean,
    errored: Boolean,
    response: HolidaysResponseDto?,
    extraSkipDates: Set<LocalDate>,
    onYearChange: (Int) -> Unit,
) {
    Column(modifier = Modifier.fillMaxWidth().gap(Spacing.sm)) {
        Row(
            modifier = Modifier.fillMaxWidth(),
            horizontalArrangement = Arrangement.SpaceBetween,
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Row(
                verticalAlignment = Alignment.CenterVertically,
                horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
            ) {
                SpanText(
                    text = "Holidays in $year",
                    modifier = Modifier
                        .fontSize(Typography.bodySmall.size)
                        .fontWeight(600)
                        .color(Palette.textPrimary),
                )
                Select(
                    value = year,
                    options = listOf(currentYear, currentYear + 1).map {
                        SelectOption(it, it.toString())
                    },
                    onValueChange = onYearChange,
                    modifier = Modifier.width(110.px),
                    ariaLabel = "Holiday preview year",
                )
            }
            response?.let {
                SpanText(
                    text = "${it.items.size} dates",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary),
                )
            }
        }

        when {
            loading -> Column(modifier = Modifier.fillMaxWidth().gap(Spacing.sm)) {
                repeat(3) { SkeletonLine(widthPct = 60, heightPx = 12) }
            }
            errored -> SpanText(
                text = "Couldn't load holidays — Jollyday may not have data for $country.",
                modifier = Modifier
                    .fontSize(Typography.caption.size)
                    .color(Palette.textTertiary),
            )
            response != null && response.items.isEmpty() -> {
                val sub = subdivision?.let { " ($it)" }.orEmpty()
                SpanText(
                    text = "No holidays found for $country$sub in $year.",
                    modifier = Modifier
                        .fontSize(Typography.caption.size)
                        .color(Palette.textTertiary),
                )
            }
            response != null -> {
                val grouped = response.items
                    .sortedBy { it.date }
                    .groupBy { it.date.monthNumber }
                    .entries
                    .sortedBy { it.key }
                Column(modifier = Modifier.fillMaxWidth()) {
                    grouped.forEachIndexed { index, entry ->
                        if (index > 0) {
                            Box(
                                modifier = Modifier
                                    .fillMaxWidth()
                                    .margin(topBottom = Spacing.xs)
                                    .styleModifier {
                                        property("height", "1px")
                                        property("background-color", Palette.borderSubtle.cssRgb())
                                    },
                            ) {}
                        }
                        HolidayMonthRow(
                            monthNumber = entry.key,
                            items = entry.value,
                            extraSkipDates = extraSkipDates,
                        )
                    }
                }
            }
        }
    }
}

@Composable
private fun HolidayMonthRow(
    monthNumber: Int,
    items: List<HolidayDto>,
    extraSkipDates: Set<LocalDate>,
) {
    Row(
        modifier = Modifier.fillMaxWidth(),
        verticalAlignment = Alignment.Top,
        horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
    ) {
        SpanText(
            text = MONTH_ABBREV_UPPER[monthNumber - 1],
            modifier = Modifier
                .width(52.px)
                .fontSize(Typography.caption.size)
                .fontWeight(600)
                .color(Palette.textTertiary)
                .styleModifier { property("letter-spacing", "0.08em") },
        )
        Column(modifier = Modifier.gap(Spacing.xs).styleModifier { property("flex", "1") }) {
            items.forEach { holiday ->
                Row(
                    modifier = Modifier.fillMaxWidth(),
                    verticalAlignment = Alignment.CenterVertically,
                    horizontalArrangement = Arrangement.spacedBy(Spacing.sm),
                ) {
                    Box(
                        modifier = Modifier
                            .width(28.px)
                            .styleModifier {
                                property("display", "inline-block")
                                property("text-align", "right")
                                property("flex-shrink", "0")
                            },
                    ) {
                        SpanText(
                            text = holiday.date.dayOfMonth.toString(),
                            modifier = Modifier
                                .fontFamily(*Typography.MONO_STACK)
                                .fontSize(Typography.caption.size)
                                .color(Palette.textTertiary),
                        )
                    }
                    SpanText(
                        text = holiday.name,
                        modifier = Modifier
                            .fontSize(Typography.bodySmall.size)
                            .color(Palette.textPrimary)
                            .styleModifier { property("flex", "1") },
                    )
                    if (holiday.date in extraSkipDates) {
                        HolidayAlsoCustomChip()
                    }
                }
            }
        }
    }
}

@Composable
private fun HolidayAlsoCustomChip() {
    Box(
        modifier = Modifier
            .padding(topBottom = Spacing.xxs, leftRight = Spacing.xs)
            .backgroundColor(Palette.surfaceSubtle)
            .borderRadius(Radius.full)
            .styleModifier { property("border", "1px solid ${Palette.borderSubtle.cssRgb()}") },
    ) {
        SpanText(
            text = "✓ also custom",
            modifier = Modifier
                .fontSize(Typography.caption.size)
                .color(Palette.textTertiary),
        )
    }
}
